<template>
    <div class="documents-tab p-3">
        <ValidationObserver ref="documentsForm">
            <b-alert show variant="info" class="d-flex align-items-center mb-4">
                <i class="ri-information-line mr-3" style="font-size: 1.5rem;"></i>
                <div>
                    <strong>{{$t('please_upload_required_documents')}}</strong><br>
                            {{$t('max_upload_file_info',{max_size:5})}}
                 </div>
            </b-alert>

            <fieldset class="border p-3 mb-4 rounded bg-white shadow-sm" v-if="!loadingParams">
                <legend class="w-auto px-2 font-weight-bold h6">{{ $t('documents')}}</legend>
                <b-table-simple hover small responsive class="border rounded text-center">
                    <b-thead head-variant="light">
                        <b-tr>
                            <b-th class="text-left font-weight-bold">{{$t('document_type')}}</b-th>
                            <b-th class="font-weight-bold">{{$t('file')}}</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="(doc, index) in requiredDocuments" :key="index">
                            <b-td class="align-middle text-left font-weight-bold">
                                {{ getLocaleText(doc, 'document_type_name') }}
                                <div v-if="formData.documents[doc.document_type]" class="small mt-1 text-success font-weight-bold">
                                    <i class="ri-check-line"></i> {{ $t('was_elected') }}
                                </div>
                                <div v-else-if="['uploaded', 'pending'].includes(doc.status)" class="small mt-1 text-success font-weight-bold">
                                    <i class="ri-checkbox-circle-fill"></i> {{ $t('status_uploaded')}}
                                </div>
                            </b-td>
                            <b-td class="align-middle text-center">
                                <div class="d-flex justify-content-center align-items-center" style="gap: 10px;">
                                    <div v-if="['uploaded', 'pending'].includes(doc.status)">
                                        <b-button variant="outline-primary" size="sm" @click="downloadDocument(doc)" class="px-3">
                                            <i class="ri-download-2-line"></i> {{ $t('download') }}
                                        </b-button>
                                    </div>

                                    <ValidationProvider :name="'files.'+index" :rules="['uploaded', 'pending'].includes(doc.status) ? '' : 'required'" v-slot="{valid, errors}">
                                        <b-form-group class="mb-0">
                                            <div class="d-flex custom-file-upload">
                                                <b-form-file
                                                    v-model="formData.documents[doc.document_type]"
                                                    :state="errors[0] ? false : (valid ? true : null)"
                                                    :placeholder="$t('select_file')"
                                                    :drop-placeholder="$t('drop_file')"
                                                    :accept="'.pdf,.jpg,.jpeg,.png'"
                                                    :ref="'fileInput_' + index"
                                                ></b-form-file>
                                                <b-button variant="outline-secondary"
                                                          @click="$refs['fileInput_' + index][0].$el.childNodes[0].click();">
                                                    {{ $t('browse') }}
                                                </b-button>
                                            </div>
                                            <div class="invalid-feedback d-block text-left mt-1" v-if="errors[0]" v-html="errors[0]"></div>
                                        </b-form-group>
                                    </ValidationProvider>
                                </div>
                            </b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>
            </fieldset>
            <div v-else class="text-center py-5">
                <b-spinner variant="primary"></b-spinner>
                <p class="mt-2 text-muted">{{$t('loading')}}</p>
            </div>
            <div class="d-flex justify-content-center mt-4">
                <b-button variant="outline-dark" @click="$emit('prev')" class="px-4 font-weight-bold shadow-sm mr-3">
                    <i class="ri-arrow-left-line"></i> {{ $t('back')}}
                </b-button>
                <b-button variant="primary" @click="submitAndNext" class="px-5 font-weight-bold shadow-sm">
                    {{ $t('next') }} <i class="ri-arrow-right-line"></i>
                </b-button>
            </div>
        </ValidationObserver>
        <b-modal ref="successModal" hide-header hide-footer centered size="md">
            <div class="d-flex flex-column align-items-center justify-content-center p-4 text-center">
                <div class="mb-3 text-info">
                    <i class="ri-information-line" style="font-size: 3.5rem;"></i>
                </div>
                <div class="message mb-4 font-weight-bold text-dark" style="font-size: 1.1rem; line-height: 1.6;">
                    {{ $t('preview_btn_info') }}
                </div>
                <b-button variant="info" class="font-weight-bold shadow-sm px-5 text-white" @click="goToSummary">
                    {{ $t('save') }}
                </b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import {ValidationObserver} from "vee-validate";
import SpecialStudentApplicationRegisterServices from "@/services/SpecialStudentApplicationRegisterServices";

export default {
    name: "DocumentsTab",
    components: {
        ValidationObserver
    },
    props: {
        countDown: {
            type:Number,
            default:0
        }
    },
    data() {
        return {
            loadingParams: false,
            parameterItems: [],
            formData:{
                documents:{}
            },
            requiredDocuments: []
        }
    },
    created() {
        this.fetchDocuments();
    },
    methods: {
        async fetchDocuments() {
            this.loadingParams = true;
            try {
                this.parameterItems = await this.$store.dispatch('fetchParameter', {
                    parameterCode: 'special_student_application_documents',
                    isPublic: true
                });
                this.requiredDocuments=this.parameterItems
                this.setDocuments()
            } catch (e) {
                this.parameterItems = [];
            } finally {
                this.loadingParams = false;
            }
        },
        setDocuments(){
            this.formData =this.getWithExpiry('specialStudentData',true)
            const documents=this.formData.documents??[]
            const newObject={}
            documents.forEach((item)=>{
                newObject[item.document_type]=null
            })
            this.formData.documents=newObject
            this.requiredDocuments=documents
        },
        async submitAndNext() {
            const isUploaded=this.requiredDocuments.every((item)=>item.status==='uploaded')
            if (isUploaded) return this.$emit('next',4)
            const isValid = await this.$refs.documentsForm.validate();
            if (!isValid)return
            this.$refs.successModal.show();
        },
        downloadDocument(document){
            const data = {
                document_type: document.document_type,
            }
            SpecialStudentApplicationRegisterServices.showDocuments(document.id,data)
                .then(res => {
                    this._downloadFile(res, this.getLocaleText(document,'document_type_name') + '.' + (res.headers.file_extension ?? 'pdf'))
                }).catch((err) => {
                this.showErrors(err, null,this.$refs.documentsForm)
            })
        },
        goToSummary() {
            this.$refs.successModal.hide();
            const formData =new FormData()
             if (this.formData.national_id){
                 formData.append('national_id',this.formData.national_id)
             }
             if (this.formData.passport_number){
                 formData.append('passport_number',this.formData.passport_number)
             }
             formData.append('pin',this.formData.pin)
             Object.entries(this.formData.documents).forEach(([key,value]) => {
                 if (value===null)return
                 formData.append('files[]',value)
                 formData.append('document_types[]',key)
             })
            SpecialStudentApplicationRegisterServices.uploadDocuments(formData).then(response => {
                this.setWithExpiry('specialStudentData', response.data.data, this.countDown,true);
                this.$toast.success(this.$t("proccess_success"));
                this.$emit('next',4);
            }).catch(e => {
                this.showErrors(e, this.$refs.documentsForm);
            })
        }
    }
}
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
</style>
